/**
 * Patient-confirm outbound for cmrDataFlow.writeMode === confirmRequired-by-patient.
 * Stages a short-lived proposal (sessionStorage) and deep-links to the existing
 * MasterChart editor (AllergiesPage). Decline clears the proposal without writing CMR.
 *
 * Requires a valid session-bound SecureLink tci-session grant covering the outbound
 * field's topic before staging. Without a grant, nothing is staged.
 *
 * Clinician/auto non-med modes are deferred (see build log).
 */

import firebaseApp from "services/firebase";
import { masterChartPath } from "constants/masterChartRoutes";
import { getActiveCmrDataFlow } from "constants/cmrFieldRegistry";
import {
  assertClientTciSessionGrant,
  outboundSectionForField,
} from "services/cmr/tciSessionConsent";

export const PATIENT_CONFIRM_WRITE_MODE = "confirmRequired-by-patient";
export const CMR_PATIENT_CONFIRM_STORAGE_KEY = "cmrPatientConfirmProposal";
export const CMR_CONFIRM_QUERY = "cmrConfirm";

/** @typedef {{ field: string, writeMode: string, proposedValue: unknown, tciId?: string, sessionId?: string, stagedAt: string }} CmrPatientConfirmProposal */

/**
 * Outbound rows on a TCI that require patient confirmation.
 */
export function listPatientConfirmOutboundFields(tciDoc) {
  const { outbound } = getActiveCmrDataFlow(tciDoc || {});
  return outbound.filter((row) => row.writeMode === PATIENT_CONFIRM_WRITE_MODE);
}

/**
 * True when this TCI's active outbound includes field with patient-confirm mode.
 */
export function isPatientConfirmOutboundField(tciDoc, fieldId) {
  const field = typeof fieldId === "string" ? fieldId.trim() : "";
  return listPatientConfirmOutboundFields(tciDoc).some((row) => row.field === field);
}

/**
 * Stage a proposal for the patient to confirm on the existing editor page.
 * Only valid when writeMode is confirmRequired-by-patient for the field AND a
 * valid tci-session SecureLink grant covers the field's topic.
 *
 * @returns {Promise<{ ok: boolean, deepLink?: string, error?: string, proposal?: object }>}
 */
export async function stageCmrPatientConfirmProposal({
  tciId,
  field,
  proposedValue,
  sessionId,
  tciDoc,
  readerUid,
  patientUid,
} = {}) {
  const fieldId = typeof field === "string" ? field.trim() : "";
  if (!fieldId) {
    return { ok: false, error: "field is required" };
  }

  if (tciDoc && !isPatientConfirmOutboundField(tciDoc, fieldId)) {
    return {
      ok: false,
      error: `Field "${fieldId}" is not configured for patient confirm on this TCI.`,
    };
  }

  const section = outboundSectionForField(fieldId);
  if (!section) {
    return { ok: false, error: `Unknown CMR section for field "${fieldId}".` };
  }

  const session =
    typeof sessionId === "string" ? sessionId.trim() : "";
  const reader =
    (typeof readerUid === "string" && readerUid.trim()) ||
    firebaseApp.auth()?.currentUser?.uid ||
    (typeof patientUid === "string" ? patientUid.trim() : "");
  const patient =
    (typeof patientUid === "string" && patientUid.trim()) || reader;

  const grant = await assertClientTciSessionGrant({
    tciSessionId: session,
    readerUid: reader,
    patientUid: patient,
    requiredSections: [section],
  });

  if (!grant.ok) {
    return {
      ok: false,
      error: `SecureLink consent required before staging CMR write (${grant.reason || "denied"}).`,
    };
  }

  /** @type {CmrPatientConfirmProposal} */
  const proposal = {
    field: fieldId,
    writeMode: PATIENT_CONFIRM_WRITE_MODE,
    proposedValue: proposedValue ?? null,
    tciId: typeof tciId === "string" ? tciId.trim() : "",
    sessionId: session,
    stagedAt: new Date().toISOString(),
  };

  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(CMR_PATIENT_CONFIRM_STORAGE_KEY, JSON.stringify(proposal));
    }
  } catch (err) {
    return { ok: false, error: err?.message || "Failed to stage proposal" };
  }

  return { ok: true, deepLink: buildPatientConfirmDeepLink(fieldId), proposal };
}

export function readCmrPatientConfirmProposal() {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(CMR_PATIENT_CONFIRM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.writeMode !== PATIENT_CONFIRM_WRITE_MODE) return null;
    if (typeof parsed.field !== "string" || !parsed.field.trim()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCmrPatientConfirmProposal() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(CMR_PATIENT_CONFIRM_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Deep-link into MasterChart editor for the given field.
 */
export function buildPatientConfirmDeepLink(fieldId) {
  const field = typeof fieldId === "string" ? fieldId.trim() : "";
  const path =
    field === "allergies"
      ? masterChartPath("allergies")
      : masterChartPath();
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${CMR_CONFIRM_QUERY}=1&field=${encodeURIComponent(field || "")}`;
}

/**
 * Normalize proposed allergy value(s) into AllergiesPage entry shape.
 * Accepts a single allergy object, array, or string allergen name.
 */
export function normalizeProposedAllergies(proposedValue) {
  if (proposedValue == null || proposedValue === "") return [];
  const asList = Array.isArray(proposedValue) ? proposedValue : [proposedValue];
  return asList
    .map((item) => {
      if (typeof item === "string") {
        const allergen = item.trim();
        if (!allergen) return null;
        return { allergen, type: "other", reaction: "", notes: "Proposed from care session" };
      }
      if (!item || typeof item !== "object") return null;
      const allergen = String(item.allergen || item.name || "").trim();
      if (!allergen) return null;
      return {
        allergen,
        type: item.type || "other",
        reaction: item.reaction || "",
        notes: item.notes || "Proposed from care session",
        ...(item.rxcui ? { rxcui: item.rxcui } : {}),
      };
    })
    .filter(Boolean);
}
